import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState as DsErrorState } from '@zagru/design-system';
import { createCorrelationId, isZagruProblem, useT } from '@zagru/platform';
import { ErrorState } from '@/components/feedback/states';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the default fallback. */
  fallback?: (error: unknown, reset: () => void) => ReactNode;
  onError?: (error: unknown, info: ErrorInfo) => void;
  /** `page` for the whole route, `inline` for a panel, `compact` for a widget. */
  variant?: 'page' | 'inline' | 'compact';
  /** Plural noun phrase for the failure copy ("shipment risk"). */
  subject?: string;
  /**
   * Clears a caught error when this value changes.
   *
   * Use it instead of a `key` when the boundary wraps something that must survive the change. Keying the boundary
   * remounts everything below it, which is right for discarding a crashed screen and wrong for anything that holds
   * state across the change — a wizard whose steps are routes, for instance.
   */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: unknown;
  /** Diagnostic id for a render failure, logged with the stack so support can find it. */
  diagnosticId: string | null;
}

/**
 * Presents a render failure without its message. A crashed component is a defect, not something the operator can
 * act on: they get a calm notice, a retry, and a diagnostic id behind "Technical details" that matches the console
 * record engineering reads.
 */
export function ErrorFallback({ error, onRetry, variant = 'page', subject, diagnosticId }: { error: unknown; onRetry?: () => void; variant?: 'page' | 'inline' | 'compact'; subject?: string; diagnosticId?: string | null }) {
  // `useT` falls back to the English catalogue, so this also renders above the providers (the root boundary).
  const t = useT();
  if (isZagruProblem(error)) return <ErrorState error={error} subject={subject} onRetry={onRetry} variant={variant} />;
  return (
    <DsErrorState
      variant={variant}
      title={subject ? t('shell.error.view.subjectTitle', { subject: `${subject.charAt(0).toUpperCase()}${subject.slice(1)}` }) : t('shell.error.view.title')}
      description={t('shell.error.view.description')}
      correlationId={diagnosticId ?? undefined}
      onRetry={onRetry}
      retryLabel={t('failure.tryAgain')}
      technicalLabel={t('failure.technicalDetails')}
      diagnosticLabel={t('failure.diagnosticId')}
      copyLabel={t('failure.copyDiagnosticId')}
      copiedLabel={t('failure.diagnosticIdCopied')}
    />
  );
}

/** Error boundary with a problem-aware, diagnostic-safe fallback and a retry (reset) action. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, diagnosticId: null };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error, diagnosticId: isZagruProblem(error) ? (error.correlationId ?? null) : createCorrelationId() };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    console.error(`[zagru] render failure (${this.state.diagnosticId ?? 'n/a'})`, error, info.componentStack);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.props.resetKey !== previous.resetKey && this.state.error != null) this.reset();
  }

  private reset = () => this.setState({ error: null, diagnosticId: null });

  override render(): ReactNode {
    if (this.state.error !== null && this.state.error !== undefined) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return <ErrorFallback error={this.state.error} onRetry={this.reset} variant={this.props.variant} subject={this.props.subject} diagnosticId={this.state.diagnosticId} />;
    }
    return this.props.children;
  }
}

/** Contains a failing widget so the rest of the screen keeps working. */
export function WidgetBoundary({ children, subject }: { children: ReactNode; subject?: string }) {
  return (
    <ErrorBoundary variant="compact" subject={subject}>
      {children}
    </ErrorBoundary>
  );
}
